'use client';
import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import { Search, Edit2, Trash2, Eye, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, X, Check, FileText, Loader2 } from 'lucide-react';
import { thesisService } from '@/lib/services/thesisService';

interface ThesisRow {
  id: string;
  title_primary: string;
  title_secondary: string;
  author_surname: string;
  author_name: string;
  student_id_number: string;
  student_email: string;
  department: string;
  doc_type: 'master' | 'phd';
  date_submission: string;
  thesis_status: string;
  primary_lang: string;
  access_rights: string;
  created_at: string;
  approved_at: string | null;
  is_exported: boolean;
  thesis_keywords: { keyword_label: string }[];
  pdf_en_path: string;
  pdf_el_path: string;
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  published: { label: 'Published', className: 'badge-approved' },
  under_review: { label: 'Under Review', className: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200' },
  sent_back: { label: 'Sent Back', className: 'badge-sentback' },
  submitted: { label: 'Submitted', className: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200' },
  draft: { label: 'Draft', className: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-50 text-gray-600 border border-gray-200' },
  approved: { label: 'Approved', className: 'badge-approved' },
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];

export default function AllThesesTable() {
  const [theses, setTheses] = useState<ThesisRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDept, setFilterDept] = useState('all');
  const [sortCol, setSortCol] = useState<'date_submission' | 'author_surname' | 'department' | 'created_at'>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [displayLang, setDisplayLang] = useState<'en' | 'el'>('en');

  useEffect(() => {
    loadTheses();
  }, []);

  const loadTheses = async () => {
    setLoading(true);
    try {
      const data = await thesisService.getAll();
      setTheses(data);
    } catch (err) {
      console.log('Load theses error:', err);
    } finally {
      setLoading(false);
    }
  };

  const uniqueDepts = Array.from(new Set(theses.map(t => t.department).filter(Boolean)));

  const filtered = useMemo(() => {
    return theses.filter(t => {
      const q = search.toLowerCase();
      const title = displayLang === 'en' ? t.title_primary : (t.title_secondary || t.title_primary);
      const author = `${t.author_surname}, ${t.author_name}`;
      const matchSearch = !q || title?.toLowerCase().includes(q) || author?.toLowerCase().includes(q);
      const matchType = filterType === 'all' || t.doc_type === filterType;
      const matchStatus = filterStatus === 'all' || t.thesis_status === filterStatus;
      const matchDept = filterDept === 'all' || t.department === filterDept;
      return matchSearch && matchType && matchStatus && matchDept;
    }).sort((a, b) => {
      let cmp = 0;
      if (sortCol === 'date_submission') cmp = (a.date_submission || '').localeCompare(b.date_submission || '');
      else if (sortCol === 'author_surname') cmp = a.author_surname.localeCompare(b.author_surname);
      else if (sortCol === 'department') cmp = a.department.localeCompare(b.department);
      else if (sortCol === 'created_at') cmp = (a.created_at || '').localeCompare(b.created_at || '');
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [theses, search, filterType, filterStatus, filterDept, sortCol, sortDir, displayLang]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  const toggleSort = (col: typeof sortCol) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }: { col: typeof sortCol }) => {
    if (sortCol !== col) return <ChevronDown size={12} className="text-[#c0c8d8]" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="text-[#1a2e5a]" /> : <ChevronDown size={12} className="text-[#1a2e5a]" />;
  };

  const toggleRow = (id: string) => setSelectedRows(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  const toggleAll = () => setSelectedRows(selectedRows.length === paginated.length ? [] : paginated.map(r => r.id));

  const handleDelete = async (id: string) => {
    const success = await thesisService.delete(id);
    if (success) {
      setTheses(prev => prev.filter(t => t.id !== id));
      setDeleteConfirmId(null);
      toast.success('Thesis deleted successfully.');
    } else {
      toast.error('Failed to delete thesis.');
    }
  };

  const handleBulkDelete = async () => {
    for (const id of selectedRows) {
      await thesisService.delete(id);
    }
    setTheses(prev => prev.filter(t => !selectedRows.includes(t.id)));
    toast.success(`${selectedRows.length} theses deleted successfully.`);
    setSelectedRows([]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-[#1a2e5a]" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-700 text-[#0f1a35]">All Theses</h2>
          <p className="text-sm text-[#4a5578] mt-0.5">
            <span className="font-600 text-[#1a2e5a]">{filtered.length}</span> of {theses.length} theses
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border border-[#e2e6f0] rounded-lg overflow-hidden text-xs">
            {(['en', 'el'] as const).map(l => (
              <button key={l} onClick={() => setDisplayLang(l)}
                className={`px-3 py-1.5 font-600 transition-all ${displayLang === l ? 'bg-[#1a2e5a] text-white' : 'bg-white text-[#4a5578] hover:bg-gray-50'}`}>
                {l === 'en' ? 'EN' : 'ΕΛ'}
              </button>
            ))}
          </div>
          {selectedRows.length > 0 && (
            <button onClick={handleBulkDelete} className="flex items-center gap-1.5 text-xs font-600 text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-all">
              <Trash2 size={12} /> Delete ({selectedRows.length})
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8892a8]" />
          <input type="text" placeholder="Search by title or author..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-8 pr-3 py-2 text-xs border border-[#e2e6f0] rounded-lg bg-white focus:outline-none focus:border-[#1a2e5a]" />
        </div>
        <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }} className="text-xs border border-[#e2e6f0] rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-[#1a2e5a]">
          <option value="all">All Types</option>
          <option value="phd">PhD</option>
          <option value="master">Master's</option>
        </select>
        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} className="text-xs border border-[#e2e6f0] rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-[#1a2e5a]">
          <option value="all">All Statuses</option>
          <option value="published">Published</option>
          <option value="submitted">Submitted</option>
          <option value="under_review">Under Review</option>
          <option value="sent_back">Sent Back</option>
        </select>
        <select value={filterDept} onChange={e => { setFilterDept(e.target.value); setPage(1); }} className="text-xs border border-[#e2e6f0] rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-[#1a2e5a]">
          <option value="all">All Departments</option>
          {uniqueDepts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#e2e6f0] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#e2e6f0] bg-[#f7f8fc]">
                <th className="px-4 py-3 text-left w-8">
                  <input type="checkbox" checked={selectedRows.length === paginated.length && paginated.length > 0} onChange={toggleAll} className="accent-[#1a2e5a]" />
                </th>
                <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide">Title</th>
                <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide cursor-pointer" onClick={() => toggleSort('author_surname')}>
                  <div className="flex items-center gap-1">Author <SortIcon col="author_surname" /></div>
                </th>
                <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide cursor-pointer" onClick={() => toggleSort('department')}>
                  <div className="flex items-center gap-1">Dept <SortIcon col="department" /></div>
                </th>
                <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide">Type</th>
                <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide cursor-pointer" onClick={() => toggleSort('created_at')}>
                  <div className="flex items-center gap-1">Submitted <SortIcon col="created_at" /></div>
                </th>
                <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide">PDFs</th>
                <th className="px-4 py-3 text-right font-600 text-[#4a5578] uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-[#8892a8]">No theses found matching your filters.</td>
                </tr>
              ) : paginated.map((t) => {
                const title = displayLang === 'en' ? t.title_primary : (t.title_secondary || t.title_primary);
                const statusCfg = STATUS_CONFIG[t.thesis_status] || STATUS_CONFIG.draft;
                return (
                  <tr key={t.id} className="border-b border-[#f0f2f8] hover:bg-[#f7f8fc] transition-colors">
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={selectedRows.includes(t.id)} onChange={() => toggleRow(t.id)} className="accent-[#1a2e5a]" />
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="font-500 text-[#0f1a35] truncate">{title}</p>
                      <p className="text-[#8892a8] text-xs mt-0.5 truncate">{t.thesis_keywords?.slice(0, 2).map((k: any) => k.keyword_label).join(', ')}</p>
                    </td>
                    <td className="px-4 py-3 text-[#4a5578] whitespace-nowrap">{t.author_surname}, {t.author_name}</td>
                    <td className="px-4 py-3 text-[#4a5578] max-w-32 truncate">{t.department}</td>
                    <td className="px-4 py-3">
                      <span className={t.doc_type === 'phd' ? 'badge-phd' : 'badge-master'}>{t.doc_type === 'phd' ? 'PhD' : "Master's"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={statusCfg.className}>{statusCfg.label}</span>
                    </td>
                    <td className="px-4 py-3 text-[#8892a8] whitespace-nowrap">{t.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${t.pdf_en_path ? 'bg-emerald-500' : 'bg-gray-300'}`} title="EN PDF" />
                        <span className={`w-2 h-2 rounded-full ${t.pdf_el_path ? 'bg-emerald-500' : 'bg-gray-300'}`} title="EL PDF" />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {deleteConfirmId === t.id ? (
                          <>
                            <button onClick={() => handleDelete(t.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Confirm delete">
                              <Check size={13} />
                            </button>
                            <button onClick={() => setDeleteConfirmId(null)} className="p-1.5 text-[#8892a8] hover:bg-gray-100 rounded-lg transition-all" title="Cancel">
                              <X size={13} />
                            </button>
                          </>
                        ) : (
                          <button onClick={() => setDeleteConfirmId(t.id)} className="p-1.5 text-[#8892a8] hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Delete">
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#e2e6f0]">
            <div className="flex items-center gap-2 text-xs text-[#8892a8]">
              <span>Rows per page:</span>
              <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} className="border border-[#e2e6f0] rounded px-2 py-1 text-xs bg-white">
                {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <span>{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of {filtered.length}</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition-all">
                <ChevronLeft size={14} />
              </button>
              <span className="text-xs text-[#4a5578] px-2">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition-all">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}